/** Adapted from standalone-search f83fe2d1 via selected Registrar V2. See docs/delivery/iteration2/fusion-shared-kit/TRANSFER.json. */
import * as React from "react";
import { ChevronDown as NavArrowDown } from "lucide-react";
import type { IconComponent } from "@/components/ui/detail-card/icon";
import { cn } from "@/lib/utils";

export type CardHue =
  | "slate"
  | "indigo"
  | "blue"
  | "violet"
  | "emerald"
  | "amber"
  | "orange";

const HUE_TILE: Record<CardHue, string> = {
  slate: "bg-muted text-muted-foreground",
  indigo: "bg-primary-wash text-primary",
  blue: "bg-primary-wash text-primary",
  violet: "bg-supporting text-supporting-foreground",
  emerald: "bg-status-success-surface text-status-success",
  amber: "bg-status-warning-surface text-status-warning",
  orange: "bg-status-warning-surface text-status-warning",
};

export interface DetailCardHeadProps {
  icon?: IconComponent;
  title: React.ReactNode;
  hue?: CardHue;
  /** Right-aligned mono counter pill. Renders with 6px gap to chevron when both present. */
  counter?: React.ReactNode;
  /** Per-card counter typography override. */
  counterClassName?: string;
  /** Adds a rotating chevron. Signals in-place expand, not navigation. */
  expandable?: boolean;
  isOpen?: boolean;
  /** Makes the head a button. Used by cards that navigate or toggle open. */
  onClick?: () => void;
  /** Override the default accessible label when onClick is present. */
  "aria-label"?: string;
  /** ID of the region controlled by an expandable head. */
  "aria-controls"?: string;
  /**
   * Inline slot rendered immediately after the title text. Use for small affordances
   * that belong with the title — e.g., an agent ID badge. If the affix contains an
   * interactive trigger and the head itself is clickable (onClick set), the affix
   * MUST stop event propagation so the head's toggle doesn't fire on affix clicks.
   */
  titleAffix?: React.ReactNode;
  className?: string;
}

export function DetailCardHead({
  icon: Icon,
  title,
  hue = "slate",
  counter,
  counterClassName,
  expandable = false,
  isOpen = false,
  onClick,
  "aria-label": ariaLabel,
  "aria-controls": ariaControls,
  titleAffix,
  className,
}: DetailCardHeadProps) {
  const content = (
    <>
      {Icon && <div
        data-card-tile
        className={cn(
          "flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-control",
          HUE_TILE[hue],
        )}
      >
        <Icon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
      </div>}
      <span className="shrink-0 text-meta font-semibold tracking-[0.01em] text-muted-foreground">
        {title}
      </span>
      {titleAffix}
      {counter !== undefined && counter !== null && (
        <span
          className={cn(
            "rounded-full bg-muted px-1.5 py-0.5 font-mono text-meta text-muted-foreground",
            !expandable && "ms-auto",
            expandable && "ms-auto me-1.5",
            counterClassName,
          )}
        >
          {counter}
        </span>
      )}
      {expandable && (
        <NavArrowDown
          data-chevron
          aria-hidden="true"
          className={cn(
            "h-3.5 w-3.5 text-muted-foreground transition-transform duration-[var(--motion-card)] ease-[var(--ease-card)]",
            !counter && "ms-auto",
            isOpen && "rotate-180",
          )}
        />
      )}
    </>
  );

  const classes = cn(
    "flex items-center gap-1.5 rounded-control",
    onClick &&
      "w-full text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card",
    className,
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        aria-expanded={expandable ? isOpen : undefined}
        aria-controls={expandable ? ariaControls : undefined}
        className={classes}
      >
        {content}
      </button>
    );
  }

  return <div className={classes}>{content}</div>;
}
